import type {
  Resource,
  UsageType,
  Contract,
  ContractTemplate,
  BookingFile,
} from '../types/loisirs'

// --- RESOURCES --------------------------------------------------------------
// The real inventory of bookable places in Windsor, Québec. Buildings hold
// child resources (rooms, ice surfaces) so filters can cascade.

export const RESOURCES: Resource[] = [
  // Centre sportif J.-A.-Lemay
  {
    id: 'res-lemay',
    name: {
      fr: 'Centre sportif J.-A.-Lemay',
      en: 'J.-A.-Lemay Sports Centre',
    },
    kind: 'plateau',
    address: '250 rue Georges-Guilbault',
    bookable: false,
    occupancy: 78,
  },
  {
    id: 'res-lemay-glace',
    parentId: 'res-lemay',
    name: {
      fr: 'Glace — Centre sportif J.-A.-Lemay',
      en: 'Ice — J.-A.-Lemay Sports Centre',
    },
    kind: 'plateau',
    address: '250 rue Georges-Guilbault',
    capacity: 400,
    rateCitizen: 145,
    rateOrganization: 95,
    occupancy: 86,
    bookable: true,
    // The ice sheet is reserved for recognized organizations only.
    bookableBy: ['organisation'],
    allowedUsageTypeIds: [
      'ut-hockey-mineur',
      'ut-hockey-adulte',
      'ut-patinage',
      'ut-luge',
      'ut-wild-pratique',
      'ut-wild-match',
      'ut-arrosage',
      'ut-patinage-libre',
    ],
  },
  {
    id: 'res-lemay-sportifs',
    parentId: 'res-lemay',
    name: { fr: 'Salle des sportifs', en: 'Athletes’ Room' },
    kind: 'plateau',
    address: '250 rue Georges-Guilbault',
    capacity: 60,
    rateCitizen: 35,
    rateOrganization: 0,
    occupancy: 41,
    bookable: true,
    bookableBy: ['organisation', 'citoyen'],
    allowedUsageTypeIds: ['ut-location-salle', 'ut-reunion'],
  },

  // Centre communautaire René-Lévesque
  {
    id: 'res-rl',
    name: {
      fr: 'Centre communautaire René-Lévesque',
      en: 'René-Lévesque Community Centre',
    },
    kind: 'plateau',
    address: '54 rue St-Georges',
    bookable: false,
    occupancy: 57,
  },
  {
    id: 'res-biblio',
    parentId: 'res-rl',
    name: { fr: 'Bibliothèque municipale', en: 'Municipal Library' },
    kind: 'plateau',
    address: '52 rue St-Georges',
    capacity: 80,
    rateCitizen: 0,
    rateOrganization: 0,
    occupancy: 34,
    bookable: true,
  },
  {
    id: 'res-local-314',
    parentId: 'res-rl',
    name: {
      fr: 'Centre communautaire local 314',
      en: 'Community Centre Room 314',
    },
    kind: 'plateau',
    address: '54 rue St-Georges',
    capacity: 30,
    rateCitizen: 25,
    rateOrganization: 0,
    occupancy: 62,
    bookable: true,
  },
  {
    id: 'res-salle-rl',
    parentId: 'res-rl',
    name: {
      fr: 'Salle Centre Communautaire René-Lévesque',
      en: 'René-Lévesque Community Hall',
    },
    kind: 'plateau',
    address: '54 rue St-Georges',
    capacity: 180,
    rateCitizen: 65,
    rateOrganization: 0,
    occupancy: 71,
    bookable: true,
  },
  {
    id: 'res-salle-biblio',
    parentId: 'res-rl',
    name: {
      fr: 'Salle d’animation de la bibliothèque',
      en: 'Library Activity Room',
    },
    kind: 'plateau',
    address: '52 rue St-Georges',
    capacity: 40,
    rateCitizen: 20,
    rateOrganization: 0,
    occupancy: 48,
    bookable: true,
  },

  // École Saint-Philippe
  {
    id: 'res-st-philippe',
    name: { fr: 'École Saint-Philippe', en: 'Saint-Philippe School' },
    kind: 'plateau',
    address: '101 rue Ambroise-Dearden',
    bookable: false,
    occupancy: 52,
  },
  {
    id: 'res-gym-st-philippe',
    parentId: 'res-st-philippe',
    name: {
      fr: 'Gymnase école St-Philippe',
      en: 'St-Philippe School Gymnasium',
    },
    kind: 'plateau',
    address: '101 rue Ambroise-Dearden',
    capacity: 120,
    rateCitizen: 45,
    rateOrganization: 0,
    occupancy: 68,
    bookable: true,
  },

  // Standalone venues
  {
    id: 'res-tournesol',
    name: {
      fr: 'École secondaire du Tournesol',
      en: 'Du Tournesol High School',
    },
    kind: 'plateau',
    address: '260 rue Georges-Guilbault',
    capacity: 200,
    rateCitizen: 55,
    rateOrganization: 0,
    occupancy: 44,
    bookable: true,
  },
  {
    id: 'res-piscine',
    name: { fr: 'Piscine municipale', en: 'Municipal Pool' },
    kind: 'plateau',
    address: '300 rue St-Georges',
    capacity: 90,
    rateCitizen: 80,
    rateOrganization: 40,
    occupancy: 73,
    bookable: true,
  },
  {
    id: 'res-patinoire',
    name: { fr: 'Patinoire Dominic-Fontaine', en: 'Dominic-Fontaine Rink' },
    kind: 'plateau',
    capacity: 100,
    rateCitizen: 0,
    rateOrganization: 0,
    occupancy: 29,
    bookable: true,
  },
  {
    id: 'res-skate',
    name: { fr: 'Parc de skate', en: 'Skate Park' },
    kind: 'plateau',
    capacity: 50,
    rateCitizen: 0,
    rateOrganization: 0,
    occupancy: 12,
    bookable: true,
  },
  {
    id: 'res-piste',
    name: {
      fr: 'Piste cyclable Annie-Perreault',
      en: 'Annie-Perreault Bike Path',
    },
    kind: 'plateau',
    occupancy: 8,
    bookable: true,
  },
  {
    id: 'res-poudriere',
    name: {
      fr: 'Poudrière de Windsor — Parc historique',
      en: 'Windsor Powder Magazine — Historic Park',
    },
    kind: 'plateau',
    address: '342 rue St-Georges',
    capacity: 150,
    rateCitizen: 90,
    rateOrganization: 45,
    occupancy: 37,
    bookable: true,
  },
  {
    id: 'res-cuisine',
    name: { fr: 'La Cuisine collective', en: 'Community Kitchen' },
    kind: 'plateau',
    address: '59 rue Saint-Georges',
    capacity: 24,
    rateCitizen: 30,
    rateOrganization: 0,
    occupancy: 55,
    bookable: true,
  },
  {
    id: 'res-arbrisseaux',
    name: {
      fr: 'Maison de la famille Les Arbrisseaux',
      en: 'Les Arbrisseaux Family House',
    },
    kind: 'plateau',
    address: '64, 3e Avenue',
    capacity: 45,
    rateCitizen: 25,
    rateOrganization: 0,
    occupancy: 61,
    bookable: true,
  },
  {
    id: 'res-conseil',
    name: {
      fr: 'Salle du conseil de l’hôtel de ville',
      en: 'City Hall Council Chamber',
    },
    kind: 'plateau',
    address: '11 rue St-Georges local 230',
    capacity: 70,
    rateCitizen: 60,
    rateOrganization: 0,
    occupancy: 42,
    bookable: true,
  },
  {
    id: 'res-baseball',
    name: { fr: 'Terrain de baseball', en: 'Baseball Field' },
    kind: 'plateau',
    capacity: 200,
    rateCitizen: 35,
    rateOrganization: 0,
    occupancy: 33,
    bookable: true,
  },
  {
    id: 'res-soccer-cj',
    name: {
      fr: 'Terrain de soccer Carmen-Juneau',
      en: 'Carmen-Juneau Soccer Field',
    },
    kind: 'plateau',
    capacity: 250,
    rateCitizen: 40,
    rateOrganization: 0,
    occupancy: 58,
    bookable: true,
  },
  {
    id: 'res-soccer-tournesol',
    name: {
      fr: 'Terrain de soccer du Tournesol',
      en: 'Du Tournesol Soccer Field',
    },
    kind: 'plateau',
    address: '260 rue Georges-Guilbault',
    capacity: 200,
    rateCitizen: 40,
    rateOrganization: 0,
    occupancy: 46,
    bookable: true,
  },
  {
    id: 'res-soccer-jl',
    name: {
      fr: 'Terrain de soccer Jacques-L’Heureux',
      en: 'Jacques-L’Heureux Soccer Field',
    },
    kind: 'plateau',
    capacity: 180,
    rateCitizen: 40,
    rateOrganization: 0,
    occupancy: 51,
    bookable: true,
  },

  // Online
  {
    id: 'res-enligne',
    name: { fr: 'En ligne', en: 'Online' },
    kind: 'enligne',
    occupancy: 22,
    bookable: true,
  },

  // Loanable equipment
  {
    id: 'res-eq-chapiteau',
    name: { fr: 'Chapiteau 6 × 12 m', en: '6 × 12 m Marquee' },
    kind: 'equipement',
    rateCitizen: 120,
    rateOrganization: 0,
    bookable: true,
  },
  {
    id: 'res-eq-son',
    name: { fr: 'Système de son portatif', en: 'Portable Sound System' },
    kind: 'equipement',
    rateCitizen: 60,
    rateOrganization: 0,
    bookable: true,
  },
  {
    id: 'res-eq-tables',
    name: { fr: 'Tables et chaises (lot de 20)', en: 'Tables and Chairs (lot of 20)' },
    kind: 'equipement',
    rateCitizen: 45,
    rateOrganization: 0,
    bookable: true,
  },
  {
    id: 'res-eq-buts',
    name: { fr: 'Buts de soccer portatifs', en: 'Portable Soccer Goals' },
    kind: 'equipement',
    rateCitizen: 0,
    rateOrganization: 0,
    bookable: true,
  },

  // Human resources
  {
    id: 'res-hum-surveillant',
    name: { fr: 'Surveillant de plateau', en: 'Facility Attendant' },
    kind: 'humain',
    rateCitizen: 32,
    rateOrganization: 32,
    bookable: true,
  },
  {
    id: 'res-hum-sauveteur',
    name: { fr: 'Sauveteur qualifié', en: 'Certified Lifeguard' },
    kind: 'humain',
    rateCitizen: 38,
    rateOrganization: 38,
    bookable: true,
  },
  // Instructors — assignable to an activity
  {
    id: 'res-hum-camille',
    name: {
      fr: 'Camille Roy — monitrice aquatique',
      en: 'Camille Roy — Aquatics Instructor',
    },
    kind: 'humain',
    rateCitizen: 34,
    rateOrganization: 34,
    bookable: true,
  },
  {
    id: 'res-hum-mathieu',
    name: {
      fr: 'Mathieu Grenier — entraîneur de hockey',
      en: 'Mathieu Grenier — Hockey Coach',
    },
    kind: 'humain',
    rateCitizen: 36,
    rateOrganization: 36,
    bookable: true,
  },
  {
    id: 'res-hum-sophie',
    name: {
      fr: 'Sophie Dubois — professeure de yoga',
      en: 'Sophie Dubois — Yoga Teacher',
    },
    kind: 'humain',
    rateCitizen: 40,
    rateOrganization: 40,
    bookable: true,
  },
  {
    id: 'res-hum-karim',
    name: {
      fr: 'Karim Haddad — animateur multisport',
      en: 'Karim Haddad — Multisport Leader',
    },
    kind: 'humain',
    rateCitizen: 30,
    rateOrganization: 30,
    bookable: true,
  },
  // Program equipment
  {
    id: 'res-eq-flotteurs',
    name: { fr: 'Flotteurs et planches (lot de 30)', en: 'Floats and Boards (lot of 30)' },
    kind: 'equipement',
    rateCitizen: 0,
    rateOrganization: 0,
    bookable: true,
  },
  {
    id: 'res-eq-tapis',
    name: { fr: 'Tapis de yoga (lot de 25)', en: 'Yoga Mats (lot of 25)' },
    kind: 'equipement',
    rateCitizen: 0,
    rateOrganization: 0,
    bookable: true,
  },
  {
    id: 'res-eq-dossards',
    name: { fr: 'Dossards et ballons', en: 'Bibs and Balls' },
    kind: 'equipement',
    rateCitizen: 0,
    rateOrganization: 0,
    bookable: true,
  },
]

// --- USAGE TYPES ------------------------------------------------------------
// Soft, desaturated palette: pastel fills with dark text for AA contrast.

export const USAGE_TYPES: UsageType[] = [
  {
    id: 'ut-hockey-mineur',
    label: { fr: 'Hockey Mineur', en: 'Minor Hockey' },
    color: '#4F46E5',
    defaultContractTemplateId: 'tpl-glace',
    defaultPricing: 'tarif',
    showOnPublicCalendar: true,
  },
  {
    id: 'ut-hockey-adulte',
    label: { fr: 'Hockey Adulte', en: 'Adult Hockey' },
    color: '#0284C7',
    defaultContractTemplateId: 'tpl-glace',
    defaultPricing: 'tarif',
    showOnPublicCalendar: true,
  },
  {
    id: 'ut-patinage',
    label: { fr: 'Patinage artistique', en: 'Figure Skating' },
    color: '#DB2777',
    defaultContractTemplateId: 'tpl-glace',
    defaultPricing: 'tarif',
    showOnPublicCalendar: true,
  },
  {
    id: 'ut-luge',
    label: { fr: 'Hockey Luge', en: 'Sledge Hockey' },
    color: '#7C3AED',
    defaultContractTemplateId: 'tpl-glace',
    defaultPricing: 'gratuit',
    showOnPublicCalendar: true,
  },
  {
    id: 'ut-wild-pratique',
    label: { fr: 'Pratique du WILD', en: 'WILD Practice' },
    color: '#0D9488',
    defaultContractTemplateId: 'tpl-glace',
    defaultPricing: 'tarif',
    showOnPublicCalendar: true,
  },
  {
    id: 'ut-wild-match',
    label: { fr: 'Wild — Match', en: 'Wild — Game' },
    color: '#B45309',
    defaultContractTemplateId: 'tpl-glace',
    defaultPricing: 'tarif',
    showOnPublicCalendar: true,
  },
  {
    id: 'ut-karate',
    label: { fr: 'Karaté', en: 'Karate' },
    color: '#CA8A04',
    defaultContractTemplateId: 'tpl-gymnase',
    defaultPricing: 'tarif',
    showOnPublicCalendar: true,
  },
  {
    id: 'ut-pickleball',
    label: { fr: 'Pickleball', en: 'Pickleball' },
    color: '#059669',
    defaultContractTemplateId: 'tpl-gymnase',
    defaultPricing: 'tarif',
    showOnPublicCalendar: true,
  },
  {
    id: 'ut-arrosage',
    label: { fr: 'Arrosage glace', en: 'Ice Resurfacing' },
    color: '#64748B',
    defaultPricing: 'gratuit',
    showOnPublicCalendar: false,
  },
  {
    id: 'ut-patinage-libre',
    label: {
      fr: 'Patinage libre école demi-glace — buts sur un côté',
      en: 'School Open Skate Half-Ice — Goals on One Side',
    },
    color: '#2563EB',
    defaultContractTemplateId: 'tpl-glace',
    defaultPricing: 'gratuit',
    showOnPublicCalendar: true,
  },
  {
    id: 'ut-location-salle',
    label: { fr: 'Location de salle', en: 'Hall Rental' },
    color: '#9333EA',
    defaultContractTemplateId: 'tpl-salle',
    defaultPricing: 'tarif',
    showOnPublicCalendar: true,
  },
  {
    id: 'ut-reunion',
    label: { fr: 'Réunion', en: 'Meeting' },
    color: '#475569',
    defaultPricing: 'gratuit',
    showOnPublicCalendar: false,
  },
]

// --- CONTRACT TEMPLATES -----------------------------------------------------

export const CONTRACT_TEMPLATES: ContractTemplate[] = [
  {
    id: 'tpl-glace',
    name: { fr: 'Location de glace', en: 'Ice Rental' },
    scope: { fr: 'Glace — J.-A.-Lemay', en: 'Ice — J.-A.-Lemay' },
    author: 'loisirs',
    eSignature: true,
    clauses: 12,
  },
  {
    id: 'tpl-salle',
    name: { fr: 'Location de salle', en: 'Hall Rental' },
    scope: { fr: 'Salles et locaux', en: 'Halls and rooms' },
    author: 'loisirs',
    eSignature: true,
    clauses: 9,
  },
  {
    id: 'tpl-gymnase',
    name: { fr: 'Location de gymnase', en: 'Gymnasium Rental' },
    scope: { fr: 'Gymnases scolaires', en: 'School gymnasiums' },
    author: 'loisirs',
    eSignature: true,
    clauses: 10,
  },
  {
    id: 'tpl-terrain',
    name: { fr: 'Location de terrain', en: 'Field Rental' },
    scope: { fr: 'Terrains extérieurs', en: 'Outdoor fields' },
    author: 'loisirs',
    eSignature: false,
    clauses: 7,
  },
  {
    id: 'tpl-entente',
    name: { fr: 'Entente pluriannuelle', en: 'Multi-year Agreement' },
    scope: { fr: 'Organismes reconnus', en: 'Recognized organizations' },
    author: 'greffe',
    eSignature: false,
    clauses: 24,
  },
]

// --- CONTRACTS --------------------------------------------------------------

export const CONTRACTS: Contract[] = [
  {
    id: 'ct-1',
    number: 'CT-2026-041',
    templateId: 'tpl-glace',
    title: 'Location de glace — J.-A.-Lemay',
    locataire: 'Association Hockey Mineur de Windsor',
    status: 'signe',
    signedBy: 'Denis Charland, président',
    signedOn: '2026-08-28',
    source: 'plateforme',
    free: false,
  },
  {
    id: 'ct-2',
    number: 'CT-2026-042',
    templateId: 'tpl-glace',
    title: 'Location de glace — J.-A.-Lemay',
    locataire: 'Club de patinage artistique de Windsor',
    status: 'envoye',
    source: 'plateforme',
    free: false,
  },
  {
    id: 'ct-3',
    number: 'CT-2026-043',
    templateId: 'tpl-glace',
    title: 'Location de glace — J.-A.-Lemay',
    locataire: 'Wild de Windsor',
    status: 'en_retard',
    source: 'plateforme',
    free: false,
  },
  {
    id: 'ct-4',
    number: 'CT-2026-044',
    templateId: 'tpl-glace',
    title: 'Location de glace — École Saint-Gabriel',
    locataire: 'École Saint-Gabriel',
    status: 'signe',
    signedBy: 'Chantal Roy, direction',
    signedOn: '2026-09-02',
    source: 'greffe',
    free: true,
  },
  {
    id: 'ct-5',
    number: 'CT-2026-045',
    templateId: 'tpl-salle',
    title: 'Location de salle — René-Lévesque',
    locataire: 'Luc Vaillancourt',
    status: 'a_generer',
    source: 'plateforme',
    free: false,
  },
  {
    id: 'ct-6',
    number: 'CT-2026-046',
    templateId: 'tpl-gymnase',
    title: 'Location de gymnase — St-Philippe',
    locataire: 'Club de karaté Windsor',
    status: 'envoye',
    source: 'plateforme',
    free: false,
  },
  {
    id: 'ct-7',
    number: 'CT-2026-047',
    templateId: 'tpl-terrain',
    title: 'Location de terrain — Carmen-Juneau',
    locataire: 'Patrick Labrie',
    status: 'a_generer',
    source: 'plateforme',
    free: false,
  },
]

// --- BOOKING FILES ----------------------------------------------------------
// Slots sit in the week of September 20–26, 2026 so the calendar opens busy.

/** Builds weekly occurrences between two September 2026 dates. */
function weekly(
  prefix: string,
  weekday: number,
  startTime: string,
  endTime: string,
  count = 4,
) {
  // September 2026: the 1st is a Tuesday, so weekday 0 (Sunday) = Sept 6.
  const firstOfKind = 6 + ((weekday - 0 + 7) % 7)
  return Array.from({ length: count }, (_, i) => {
    const day = firstOfKind + i * 7
    return {
      id: `${prefix}-${i}`,
      date: `2026-09-${String(day).padStart(2, '0')}`,
      startTime,
      endTime,
    }
  }).filter((slot) => Number(slot.date.slice(-2)) <= 30)
}

export const BOOKING_FILES: BookingFile[] = [
  {
    id: 'bk-1913',
    number: 1913,
    client: {
      id: 'cl-ahmw',
      name: 'Association Hockey Mineur de Windsor',
      kind: 'organisation',
      phone: '819 845-7888',
    },
    attentionOf: 'Denis Charland',
    resourceId: 'res-lemay-glace',
    usageTypeId: 'ut-hockey-mineur',
    startDate: '2026-09-06',
    endDate: '2026-12-20',
    billingDate: '2026-10-01',
    slots: [
      ...weekly('bk1913-sat', 6, '08:00', '11:00'),
      ...weekly('bk1913-tue', 2, '18:00', '20:00'),
    ],
    status: 'active',
    author: 'Sophie Bergeron',
    noteResponsable:
      'Accès au vestiaire 3 réservé. Clé remise au responsable le 5 septembre.',
    noteFacture: 'Facturation mensuelle selon l’entente 2026-2028.',
    contractId: 'ct-1',
    attachments: [
      { name: 'Entente-AHMW-2026.pdf', size: '412 Ko' },
      { name: 'Assurance-responsabilite.pdf', size: '188 Ko' },
    ],
    billing: { total: 18240, invoiced: 6080, paid: 6080, balance: 12160 },
    payment: {
      method: 'interac',
      status: 'paye',
      reference: 'INT-77120',
      paidOn: '2026-09-04',
    },
    publicCalendar: true,
  },
  {
    id: 'bk-1914',
    number: 1914,
    client: {
      id: 'cl-cpaw',
      name: 'Club de patinage artistique de Windsor',
      kind: 'organisation',
      phone: '819 845-2214',
    },
    attentionOf: 'Marie-Josée Custeau',
    resourceId: 'res-lemay-glace',
    usageTypeId: 'ut-patinage',
    startDate: '2026-09-07',
    endDate: '2026-12-18',
    billingDate: '2026-10-01',
    slots: [
      ...weekly('bk1914-mon', 1, '16:00', '18:00'),
      ...weekly('bk1914-thu', 4, '16:00', '18:00'),
    ],
    status: 'active',
    author: 'Sophie Bergeron',
    noteResponsable: 'Barrières de protection à installer avant chaque séance.',
    contractId: 'ct-2',
    attachments: [{ name: 'Liste-patineurs-2026.xlsx', size: '64 Ko' }],
    billing: { total: 9120, invoiced: 3040, paid: 0, balance: 9120 },
    payment: {
      method: 'interac',
      status: 'virement_attendu',
      reference: 'INT-77455',
    },
    publicCalendar: true,
  },
  {
    id: 'bk-1915',
    number: 1915,
    client: {
      id: 'cl-wild',
      name: 'Wild de Windsor',
      kind: 'organisation',
      phone: '819 845-9090',
    },
    attentionOf: 'Jean-François Tardif',
    resourceId: 'res-lemay-glace',
    usageTypeId: 'ut-wild-match',
    startDate: '2026-09-12',
    endDate: '2027-03-14',
    slots: [...weekly('bk1915-sat', 6, '19:30', '22:00')],
    status: 'active',
    author: 'Martin Pelletier',
    noteResponsable: 'Billetterie et cantine ouvertes 1 h avant la mise au jeu.',
    noteFacture: 'Frais de surveillance inclus.',
    contractId: 'ct-3',
    attachments: [],
    billing: { total: 7250, invoiced: 7250, paid: 3625, balance: 3625 },
    payment: { method: 'comptoir', status: 'impaye' },
    publicCalendar: true,
  },
  {
    id: 'bk-1916',
    number: 1916,
    client: {
      id: 'cl-stgab',
      name: 'École Saint-Gabriel',
      kind: 'organisation',
      phone: '819 845-3311',
    },
    attentionOf: 'Chantal Roy',
    resourceId: 'res-lemay-glace',
    usageTypeId: 'ut-patinage-libre',
    startDate: '2026-09-09',
    endDate: '2026-12-16',
    slots: [...weekly('bk1916-wed', 3, '13:00', '14:30')],
    status: 'active',
    author: 'Sophie Bergeron',
    noteResponsable:
      'Demi-glace, buts regroupés d’un seul côté. Aucun frais — entente scolaire.',
    contractId: 'ct-4',
    attachments: [{ name: 'Entente-scolaire-signee.pdf', size: '256 Ko' }],
    billing: { total: 0, invoiced: 0, paid: 0, balance: 0 },
    payment: { method: 'exempt', status: 'paye' },
    publicCalendar: true,
  },
  {
    id: 'bk-1917',
    number: 1917,
    client: {
      id: 'cl-ville',
      name: 'Ville de Windsor',
      kind: 'organisation',
      phone: '819 845-7888',
    },
    resourceId: 'res-lemay-glace',
    usageTypeId: 'ut-arrosage',
    startDate: '2026-09-01',
    endDate: '2027-04-30',
    slots: [
      ...weekly('bk1917-sun', 0, '00:00', '23:59'),
      ...weekly('bk1917-wed', 3, '00:00', '23:59'),
    ],
    status: 'active',
    author: 'Service des travaux publics',
    noteResponsable: 'Bloc interne — glace indisponible à la location.',
    attachments: [],
    billing: { total: 0, invoiced: 0, paid: 0, balance: 0 },
    payment: { method: 'exempt', status: 'paye' },
    publicCalendar: false,
  },
  {
    id: 'bk-1918',
    number: 1918,
    client: {
      id: 'cl-luc',
      name: 'Luc Vaillancourt',
      kind: 'citoyen',
      phone: '819 566-4412',
    },
    resourceId: 'res-salle-rl',
    usageTypeId: 'ut-location-salle',
    startDate: '2026-09-26',
    endDate: '2026-09-26',
    billingDate: '2026-09-15',
    slots: [
      { id: 'bk1918-1', date: '2026-09-26', startTime: '17:00', endTime: '23:00' },
    ],
    status: 'brouillon',
    author: 'Comptoir des loisirs',
    noteResponsable: 'Réception de mariage — 140 convives attendus.',
    contractId: 'ct-5',
    attachments: [],
    billing: { total: 390, invoiced: 0, paid: 0, balance: 390 },
    payment: { method: 'credit', status: 'impaye' },
    publicCalendar: false,
  },
  {
    id: 'bk-1919',
    number: 1919,
    client: {
      id: 'cl-karate',
      name: 'Club de karaté Windsor',
      kind: 'organisation',
      phone: '819 845-6677',
    },
    attentionOf: 'Sensei Alain Dubois',
    resourceId: 'res-gym-st-philippe',
    usageTypeId: 'ut-karate',
    startDate: '2026-09-08',
    endDate: '2026-12-15',
    billingDate: '2026-10-01',
    slots: [...weekly('bk1919-tue', 2, '18:30', '20:00')],
    status: 'active',
    author: 'Martin Pelletier',
    contractId: 'ct-6',
    attachments: [],
    billing: { total: 1350, invoiced: 450, paid: 450, balance: 900 },
    payment: {
      method: 'credit',
      status: 'paye',
      reference: 'VISA-4417',
      paidOn: '2026-09-03',
    },
    publicCalendar: true,
  },
  {
    id: 'bk-1920',
    number: 1920,
    client: {
      id: 'cl-patrick',
      name: 'Patrick Labrie',
      kind: 'citoyen',
      phone: '819 566-8821',
    },
    resourceId: 'res-soccer-cj',
    usageTypeId: 'ut-pickleball',
    startDate: '2026-09-19',
    endDate: '2026-09-19',
    slots: [
      { id: 'bk1920-1', date: '2026-09-19', startTime: '10:00', endTime: '12:00' },
    ],
    status: 'facturee',
    author: 'Comptoir des loisirs',
    contractId: 'ct-7',
    attachments: [],
    billing: { total: 80, invoiced: 80, paid: 80, balance: 0 },
    payment: {
      method: 'interac',
      status: 'paye',
      reference: 'INT-78003',
      paidOn: '2026-09-11',
    },
    publicCalendar: true,
  },
  {
    id: 'bk-1921',
    number: 1921,
    client: {
      id: 'cl-stefan',
      name: 'Stéfan Paquin',
      kind: 'citoyen',
      phone: '819 566-1179',
    },
    resourceId: 'res-lemay-glace',
    usageTypeId: 'ut-hockey-adulte',
    startDate: '2026-09-11',
    endDate: '2026-12-18',
    slots: [...weekly('bk1921-fri', 5, '21:00', '22:30')],
    status: 'active',
    author: 'Comptoir des loisirs',
    noteResponsable: 'Ligue de garage — 18 joueurs inscrits.',
    attachments: [],
    billing: { total: 2320, invoiced: 580, paid: 0, balance: 2320 },
    payment: {
      method: 'interac',
      status: 'virement_attendu',
      reference: 'INT-78240',
    },
    publicCalendar: true,
  },
  {
    id: 'bk-1922',
    number: 1922,
    client: {
      id: 'cl-ville',
      name: 'Ville de Windsor',
      kind: 'organisation',
      phone: '819 845-7888',
    },
    attentionOf: 'Direction des loisirs',
    resourceId: 'res-local-314',
    usageTypeId: 'ut-reunion',
    startDate: '2026-09-22',
    endDate: '2026-09-22',
    slots: [
      { id: 'bk1922-1', date: '2026-09-22', startTime: '19:00', endTime: '21:00' },
    ],
    status: 'active',
    author: 'Sophie Bergeron',
    noteResponsable: 'Comité consultatif des loisirs — ordre du jour transmis.',
    attachments: [],
    billing: { total: 0, invoiced: 0, paid: 0, balance: 0 },
    payment: { method: 'exempt', status: 'paye' },
    publicCalendar: false,
  },
  {
    id: 'bk-1923',
    number: 1923,
    client: {
      id: 'cl-wild',
      name: 'Wild de Windsor',
      kind: 'organisation',
      phone: '819 845-9090',
    },
    resourceId: 'res-lemay-glace',
    usageTypeId: 'ut-wild-pratique',
    startDate: '2026-09-10',
    endDate: '2026-12-17',
    slots: [...weekly('bk1923-thu', 4, '20:00', '21:30')],
    status: 'active',
    author: 'Martin Pelletier',
    attachments: [],
    billing: { total: 3420, invoiced: 1140, paid: 1140, balance: 2280 },
    payment: {
      method: 'comptoir',
      status: 'paye',
      paidOn: '2026-09-08',
    },
    publicCalendar: true,
  },
  {
    id: 'bk-1924',
    number: 1924,
    client: {
      id: 'cl-ahmw',
      name: 'Association Hockey Mineur de Windsor',
      kind: 'organisation',
      phone: '819 845-7888',
    },
    resourceId: 'res-lemay-glace',
    usageTypeId: 'ut-luge',
    startDate: '2026-09-20',
    endDate: '2026-11-29',
    slots: [...weekly('bk1924-sun', 0, '14:00', '15:30', 3)],
    status: 'active',
    author: 'Sophie Bergeron',
    noteResponsable: 'Programme adapté — rampe d’accès au banc requise.',
    attachments: [],
    billing: { total: 0, invoiced: 0, paid: 0, balance: 0 },
    payment: { method: 'exempt', status: 'paye' },
    publicCalendar: true,
  },
  {
    id: 'bk-1925',
    number: 1925,
    client: {
      id: 'cl-arbrisseaux',
      name: 'Maison de la famille Les Arbrisseaux',
      kind: 'organisation',
      phone: '819 845-5544',
    },
    resourceId: 'res-salle-biblio',
    usageTypeId: 'ut-reunion',
    startDate: '2026-09-24',
    endDate: '2026-09-24',
    slots: [
      { id: 'bk1925-1', date: '2026-09-24', startTime: '09:30', endTime: '11:30' },
    ],
    status: 'active',
    author: 'Comptoir des loisirs',
    attachments: [],
    billing: { total: 0, invoiced: 0, paid: 0, balance: 0 },
    payment: { method: 'exempt', status: 'paye' },
    publicCalendar: true,
  },
]
